import React, { useMemo } from "react";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MiniDoubleSlider from '../../shared/Sliders/MiniDoubleSlider';
import { MimshakData } from '../../interfaces/DTO/mimshakData';
import { fundService } from '../../services/fundService';
import { PreferenceDistributionData } from '../../interfaces/DTO/contactPreferences';
import { FundSumDropMap } from '../../interfaces/DTO/simulation';
import { ChangesDataUtils } from './Reports/ChangesDataUtils';

type MetricKey = 'stock_market_percent' | 'foreign_currency_exposure_percent' | 'foreign_exposure_percent';

interface Props {
  data: MimshakData[];
  originalData?: MimshakData[];
  fundSumDropMap?: FundSumDropMap;
  preferenceData?: PreferenceDistributionData;
  onPreferenceChange?: (newData: PreferenceDistributionData) => void;
  isActive?: boolean;
  onCardClick?: () => void;
  onMetricClick?: (metric: MetricKey) => void;
  activeMetric?: MetricKey | null;
}

const ExposureAfterChangeSummary: React.FC<Props> = ({
  data,
  originalData,
  fundSumDropMap,
  preferenceData,
  onPreferenceChange,
  isActive = false,
  onCardClick,
  onMetricClick,
  activeMetric,
}) => {
  const { stockExposure, currencyExposure, foreignExposure } = useMemo(() => {
    const allPaths: { path_code: number; accumulated_amount: number }[] = [];

    for (const row of data) {
      const policies = row.data?.products?.flatMap(p => p.policies_or_accounts) || [];

      const additions = fundSumDropMap?.[row.policy_number];
      const extra = ChangesDataUtils.getTotalFundAddition(additions);

      const allRowPaths = policies.flatMap(policy => policy.investment_paths || []);
      const extraPerPath = allRowPaths.length > 0 ? extra / allRowPaths.length : 0;

      for (const path of allRowPaths) {
        if (path.accumulated_amount !== undefined) {
          allPaths.push({
            path_code: path.path_code,
            accumulated_amount: path.accumulated_amount + extraPerPath,
          });
        }
      }
    }

    let totalAccumulatedAmount = 0;
    let weightedStock = 0;
    let weightedCurrency = 0;
    let weightedForeign = 0;

    for (const path of allPaths) {
      const fund = fundService.getFundFromCacheById(path.path_code);
      if (fund && fund.total_assets && path.accumulated_amount) {
        const amount = path.accumulated_amount;
        totalAccumulatedAmount += amount;

        if (fund.stock_market_exposure !== undefined) {
          weightedStock += (fund.stock_market_exposure / fund.total_assets) * amount;
        }
        if (fund.foreign_currency_exposure !== undefined) {
          weightedCurrency += (fund.foreign_currency_exposure / fund.total_assets) * amount;
        }
        if (fund.foreign_exposure !== undefined) {
          weightedForeign += (fund.foreign_exposure / fund.total_assets) * amount;
        }
      }
    }

    return {
      stockExposure: totalAccumulatedAmount ? weightedStock / totalAccumulatedAmount : 0,
      currencyExposure: totalAccumulatedAmount ? weightedCurrency / totalAccumulatedAmount : 0,
      foreignExposure: totalAccumulatedAmount ? weightedForeign / totalAccumulatedAmount : 0,
    };
  }, [data, originalData, fundSumDropMap]);

  // Helper to convert indices to min/max percentages
  const getRange = (indices: number[] | undefined): { min: number; max: number } => {
    if (!indices || indices.length === 0) return { min: 0, max: 100 };
    const minIndex = Math.min(...indices);
    const maxIndex = Math.max(...indices);
    return { min: minIndex * 10, max: (maxIndex + 1) * 10 };
  };

  const stockRange = getRange(preferenceData?.stockExposure);
  const currencyRange = getRange(preferenceData?.currencyExposure);
  const foreignRange = getRange(preferenceData?.foreignExposure);

  // Same coloring logic as InteractiveGauge.getTextColor()
  const getExposureColor = (exposurePercent: number, indices: number[] | undefined): string => {
    if (!indices || indices.length === 0) return '#000';
    const needleBlockIndex = Math.floor(exposurePercent / 10);
    const selectedSet = new Set(indices);
    if (selectedSet.has(needleBlockIndex)) return '#2E7D32'; // green
    const minDistance = Math.min(...indices.map(i => Math.abs(i - needleBlockIndex)));
    return minDistance <= 2 ? '#FF9800' : '#D32F2F'; // orange or red
  };

  const renderRow = (
    label: string,
    exposure: number,
    range: { min: number; max: number },
    indices: number[] | undefined,
    metricKey: MetricKey
  ) => {
    const exposurePercent = exposure * 100;
    const color = getExposureColor(exposurePercent, indices);
    const isActive = activeMetric === metricKey;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <Tooltip title={`הצג ${label} בגרף`}>
            <IconButton
              onClick={(e) => { e.stopPropagation(); onMetricClick?.(metricKey); }}
              color={isActive ? 'primary' : 'default'}
              size="small"
              sx={{
                border: isActive ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ShowChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="textSecondary">
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: indices && indices.length > 0 ? color : 'text.primary' }}>
              {exposurePercent.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
        {preferenceData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              טווח העדפה
            </Typography>
            <MiniDoubleSlider
              min={0}
              max={100}
              rangeMin={range.min}
              rangeMax={range.max}
              onChange={() => {}}
              step={10}
              width={100}
              disabled={true}
              currentValue={exposurePercent}
              currentValueColor={color}
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right' }}>
              {range.min}% - {range.max}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Paper
      onClick={onCardClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        backgroundColor: '#fffbe6',
        outline: isActive ? '2px solid #f59e0b' : 'none',
        transition: 'outline 0.2s',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'right' }}>
        חשיפות משוקללות לאחר שינוי
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderRow('חשיפה למניות', stockExposure, stockRange, preferenceData?.stockExposure, 'stock_market_percent')}
        {renderRow('חשיפה למט"ח', currencyExposure, currencyRange, preferenceData?.currencyExposure, 'foreign_currency_exposure_percent')}
        {renderRow('חשיפה לחו"ל', foreignExposure, foreignRange, preferenceData?.foreignExposure, 'foreign_exposure_percent')}
      </Box>
    </Paper>
  );
};

export default ExposureAfterChangeSummary;
